/**
 * Weekly Movers — identifies users who gained the most rank positions this week.
 * Uses AsyncStorage rank snapshots from rankHistory if available.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

const PREV_RANKS_KEY = 'duel_leaderboard_prev_ranks';

export interface WeeklyMover {
  userId: string;
  displayName: string;
  handle: string | null;
  currentRank: number;
  previousRank: number;
  rankChange: number; // positive = moved up
}

/**
 * Compute top N biggest movers by comparing current ranks against previous snapshot.
 * @param currentRanked - array of { userId, displayName, handle } in current rank order
 * @param topN - how many movers to return (default 3)
 */
export async function getWeeklyMovers(
  currentRanked: { userId: string; displayName: string; handle: string | null }[],
  topN: number = 3,
): Promise<WeeklyMover[]> {
  if (currentRanked.length < 3) return [];

  let prevRanks: Record<string, number> = {};
  try {
    const raw = await AsyncStorage.getItem(PREV_RANKS_KEY);
    if (raw) prevRanks = JSON.parse(raw);
  } catch {
    return [];
  }

  if (Object.keys(prevRanks).length === 0) return [];

  const movers: WeeklyMover[] = [];

  currentRanked.forEach((user, idx) => {
    const currentRank = idx + 1;
    const previousRank = prevRanks[user.userId];
    if (previousRank != null && previousRank > currentRank) {
      movers.push({
        userId: user.userId,
        displayName: user.displayName,
        handle: user.handle,
        currentRank,
        previousRank,
        rankChange: previousRank - currentRank,
      });
    }
  });

  movers.sort((a, b) => b.rankChange - a.rankChange);
  return movers.slice(0, topN);
}
